"use client";

import { useState, useSyncExternalStore } from "react";

import {
  getConsentServerSnapshot,
  getConsentSnapshot,
  setConsent,
  subscribeToConsent,
} from "@/shared/lib/consent";

/**
 * Cookie consent banner.
 *
 * Pairs with the Consent Mode defaults pushed in GoogleTagManager. Those
 * default every signal to denied before the container loads; this is only the
 * UI for changing that.
 *
 * Set NEXT_PUBLIC_CONSENT_BANNER="0" to disable.
 */

const ENABLED = process.env.NEXT_PUBLIC_CONSENT_BANNER !== "0";

export default function ConsentBanner() {
  const stored = useSyncExternalStore(
    subscribeToConsent,
    getConsentSnapshot,
    getConsentServerSnapshot,
  );

  // Tracks the click that dismisses the banner. Separate from `stored` so the
  // banner disappears the instant either button is pressed, without waiting on
  // a storage round-trip that may be blocked entirely in private mode.
  const [dismissed, setDismissed] = useState(false);

  if (!ENABLED || dismissed || stored !== null) return null;

  const choose = (choice: "granted" | "denied") => {
    setDismissed(true);
    setConsent(choice);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[9998] p-3 sm:p-4"
    >
      <div className="mx-auto flex max-w-2xl flex-col gap-3 rounded-2xl border-2 border-[#4c4c4c] bg-white p-4 shadow-[4px_4px_0_#4c4c4c] sm:flex-row sm:items-center sm:gap-4">
        <p className="flex-1 font-patrick text-[15px] leading-snug text-[#2d2d2d]">
          We use cookies to understand how Hoku is used. Analytics stay off
          unless you allow them.
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => choose("denied")}
            className="flex-1 rounded-full border border-[#4c4c4c]/40 bg-white px-5 py-2 font-winky text-[13px] font-bold text-[#6b6b6b] transition-colors hover:border-[#4c4c4c] hover:text-[#2d2d2d] sm:flex-none"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            className="flex-1 rounded-full border-2 border-[#4c4c4c] bg-[#ffd372] px-5 py-2 font-winky text-[13px] font-bold text-[#2d2d2d] shadow-[3px_3px_0_#4c4c4c] transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[1px_1px_0_#4c4c4c] sm:flex-none"
          >
            Allow
          </button>
        </div>
      </div>
    </div>
  );
}
